// Petits composants partagés du module interventions.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  BadgeCheck, ClipboardCheck, Eye, FileText, Gauge, History, Lightbulb, ListChecks, ShieldAlert, Sparkles, StickyNote, Users, Wrench,
} from 'lucide-react';
import { Badge } from '../../components/ui.tsx';
import { date } from '../../lib/format.ts';
import type { Element, SourceIntervention, TypeElement } from './types.ts';
import { LIBELLES_ELEMENT } from './types.ts';

/**
 * Lien vers le lecteur à la bonne page ET sur l'extrait cité (paramètre « extrait »).
 * Même rendu que <Source/> de ui.tsx, qui ne transmet pas encore l'extrait.
 */
export function SourceExtrait({ versionId, page, chunkId, titre, localisation, version }:
  { versionId: string | null; page: number | null; chunkId?: string | null; titre: string; localisation?: string | null; version?: string | null }) {
  const texte = `${titre}${version ? `, version ${version}` : ''}${page ? ` — p. ${page}` : ''}${localisation ? `, ${localisation}` : ''}`;
  if (!versionId) return <span className="source" title="Source non accessible"><FileText aria-hidden /><span>{texte}</span></span>;
  const q = new URLSearchParams();
  if (page) q.set('page', String(page));
  if (chunkId) q.set('extrait', chunkId);
  return (
    <Link className="source int-source" to={`/documentation/lecteur/${versionId}${q.size ? `?${q}` : ''}`} title={`Ouvrir ${texte}`}>
      <FileText aria-hidden /><span>{texte}</span>
    </Link>
  );
}

/** Source « Déjà vu » d'une hypothèse : lien vers le rapport de l'intervention passée, si ce lecteur peut l'ouvrir. */
export function LienInterventionSource({ s }: { s: SourceIntervention }) {
  const texte = s.reference ? `Déjà vu : ${s.reference}${s.date ? ` du ${date(s.date)}` : ''}` : 'Déjà vu : intervention source non accessible';
  if (!s.id) return <span className="source" title={texte}><History aria-hidden /><span>{texte}</span></span>;
  return (
    <Link className="source int-source" to={`/interventions/${s.id}/rapport`} title={`Ouvrir le rapport ${s.reference ?? ''}`}>
      <History aria-hidden /><span>{texte}</span>
    </Link>
  );
}

export const ICONES_ELEMENT: Record<TypeElement, ReactNode> = {
  hypothese: <Lightbulb aria-hidden />, controle_propose: <ListChecks aria-hidden />, controle_realise: <ClipboardCheck aria-hidden />,
  cause_confirmee: <BadgeCheck aria-hidden />, mesure: <Gauge aria-hidden />, observation: <Eye aria-hidden />, operation: <Wrench aria-hidden />,
  escalade: <Users aria-hidden />, note: <StickyNote aria-hidden />, securite: <ShieldAlert aria-hidden />,
};

/** Étiquette du type d'élément : icône + texte + couleur (jamais la couleur seule). */
export function EtiquetteType({ kind }: { kind: TypeElement }) {
  return <span className={`int-type int-type-${kind}`}>{ICONES_ELEMENT[kind]}<span>{LIBELLES_ELEMENT[kind]}</span></span>;
}

export function BadgeOrigine({ el }: { el: Pick<Element, 'origin'> }) {
  if (el.origin === 'ia') return <Badge ton="info" icone={<Sparkles size={14} aria-hidden />}>Proposé par l’assistance</Badge>;
  if (el.origin === 'regle') return <Badge ton="neutre" icone={<FileText size={14} aria-hidden />}>Piste documentée</Badge>;
  return null;
}

/** Légende de la chronologie : les quatre étapes du raisonnement à ne jamais confondre. */
export function LegendeDiagnostic() {
  const types: TypeElement[] = ['hypothese', 'controle_propose', 'controle_realise', 'cause_confirmee'];
  return (
    <ul className="int-legende" aria-label="Légende">
      {types.map((t) => <li key={t}><EtiquetteType kind={t} /></li>)}
    </ul>
  );
}
